import { ChangeEvent, FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'

import { isNotEmpty } from '../../../../../_metronic/helpers'
import { initialHawalaBranch, HawalaBranch } from '../core/_models'
import { useListView } from '../core/ListViewProvider'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createHawalaBranch, updateHawalaBranch } from '../core/_requests'
import { HawalaBranchesListLoading } from '../components/loading/HawalaBranchesListLoading'

type Props = {
  isHawalaBranchLoading: boolean
  hawala_branch: HawalaBranch
}

const editHawalaBranchSchema = Yup.object().shape({
  name: Yup.string().required('نام ضروری است'),
  email: Yup.string().email('ایمیل معتبر نیست').nullable(),
  phone_number: Yup.string().nullable(),
  address: Yup.string().nullable(),
  commission_type: Yup.string().oneOf(['percentage', 'fixed']).required('نوع کمیسیون الزامی است'),
  amount: Yup.number().min(0).required('مقدار کمیسیون الزامی است'),
  status: Yup.string().oneOf(['active', 'inactive']).required('وضعیت الزامی است'),
})

const HawalaBranchEditModalForm: FC<Props> = ({ hawala_branch, isHawalaBranchLoading }) => {
  const intl = useIntl()
  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  const [hawala_branchForEdit] = useState<HawalaBranch>({
    ...initialHawalaBranch,
    ...hawala_branch,
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) refetch()
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: hawala_branchForEdit,
    validationSchema: editHawalaBranchSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updateHawalaBranch(values)
        } else {
          await createHawalaBranch(values)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  return (
    <>
      <form className='form' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column scroll-y me-n7 pe-7'>
          {/* Name */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>
              <FormattedMessage id='HAWALABRANCH.NAME.LABEL' defaultMessage='نام برنچ' />
            </label>
            <input
              type='text'
              {...formik.getFieldProps('name')}
              className={clsx('form-control form-control-solid', {
                'is-invalid': formik.touched.name && formik.errors.name,
                'is-valid': formik.touched.name && !formik.errors.name,
              })}
              placeholder={intl.formatMessage({ id: 'HAWALABRANCH.NAME.PLACEHOLDER', defaultMessage: 'نام برنچ را وارد کنید' })}
              disabled={formik.isSubmitting || isHawalaBranchLoading}
            />
          </div>

          {/* Email */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>
              <FormattedMessage id='HAWALABRANCH.EMAIL.LABEL' defaultMessage='ایمیل' />
            </label>
            <input
              type='email'
              {...formik.getFieldProps('email')}
              className='form-control form-control-solid'
              placeholder={intl.formatMessage({ id: 'HAWALABRANCH.EMAIL.PLACEHOLDER', defaultMessage: 'ایمیل (اختیاری)' })}
              disabled={formik.isSubmitting || isHawalaBranchLoading}
            />
          </div>

          {/* Phone Number */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>
              <FormattedMessage id='HAWALABRANCH.PHONE.LABEL' defaultMessage='شماره تماس' />
            </label>
            <input
              type='text'
              {...formik.getFieldProps('phone_number')}
              className='form-control form-control-solid'
              placeholder={intl.formatMessage({ id: 'HAWALABRANCH.PHONE.PLACEHOLDER', defaultMessage: 'شماره تماس' })}
              disabled={formik.isSubmitting || isHawalaBranchLoading}
            />
          </div>

          {/* Address */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>
              <FormattedMessage id='HAWALABRANCH.ADDRESS.LABEL' defaultMessage='آدرس' />
            </label>
            <input
              type='text'
              {...formik.getFieldProps('address')}
              className='form-control form-control-solid'
              placeholder={intl.formatMessage({ id: 'HAWALABRANCH.ADDRESS.PLACEHOLDER', defaultMessage: 'آدرس را وارد کنید' })}
              disabled={formik.isSubmitting || isHawalaBranchLoading}
            />
          </div>

          {/* Commission Type */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>
              <FormattedMessage id='HAWALABRANCH.TYPE.LABEL' defaultMessage='نوع کمیسیون' />
            </label>
            <select
              {...formik.getFieldProps('commission_type')}
              className='form-select form-select-solid'
              disabled={formik.isSubmitting || isHawalaBranchLoading}
            >
              <option value='percentage'>درصدی</option>
              <option value='fixed'>ثابت</option>
            </select>
          </div>

          {/* Commission Amount */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>
              <FormattedMessage id='HAWALABRANCH.AMOUNT.LABEL' defaultMessage='مقدار کمیسیون' />
            </label>
            <input
              type='number'
              {...formik.getFieldProps('amount')}
              className='form-control form-control-solid'
              placeholder={intl.formatMessage({ id: 'HAWALABRANCH.AMOUNT.PLACEHOLDER', defaultMessage: 'مقدار را وارد کنید' })}
              disabled={formik.isSubmitting || isHawalaBranchLoading}
            />
          </div>

          {/* Status */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>
              <FormattedMessage id='HAWALABRANCH.STATUS.LABEL' defaultMessage='وضعیت' />
            </label>
            <select
              {...formik.getFieldProps('status')}
              className='form-select form-select-solid'
              disabled={formik.isSubmitting || isHawalaBranchLoading}
            >
              <option value='active'>فعال</option>
              <option value='inactive'>غیرفعال</option>
            </select>
          </div>
        </div>

        {/* Buttons */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            disabled={formik.isSubmitting || isHawalaBranchLoading}
          >
            <FormattedMessage id='FORM.GENERAL.DISCARD' defaultMessage='لغو' />
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            disabled={formik.isSubmitting || isHawalaBranchLoading}
          >
            <span className='indicator-label'>
              <FormattedMessage id='FORM.GENERAL.SUBMIT' defaultMessage='ثبت' />
            </span>
            {(formik.isSubmitting || isHawalaBranchLoading) && (
              <span className='indicator-progress'>
                <FormattedMessage id='FORM.GENERAL.PLEASEWAIT' defaultMessage='لطفاً منتظر بمانید' />
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>

      {(formik.isSubmitting || isHawalaBranchLoading) && <HawalaBranchesListLoading />}
    </>
  )
}

export { HawalaBranchEditModalForm }
